import { useEffect, useRef } from 'react';
import { spore } from '@/spore/runtime';
import { Panel } from './Panel';

const WINDOW_MS = 8000;
const GESTURE_COLORS = { PULSE: '#ff4d4d', STROKE: '#ffb000', STILL: '#00e5ff' };

function draw(ctx, w, h) {
  const now = spore.now();
  const th = spore.engine.getThresholds();
  const x = (t) => w - ((now - t) / WINDOW_MS) * w;
  const y = (v) => h - 8 - (v / 127) * (h - 16);
  ctx.clearRect(0, 0, w, h);

  ctx.strokeStyle = 'rgba(0,229,255,0.06)';
  ctx.lineWidth = 1;
  for (let i = 1; i < 8; i += 1) {
    ctx.beginPath();
    ctx.moveTo((w / 8) * i, 0);
    ctx.lineTo((w / 8) * i, h);
    ctx.stroke();
  }
  [32, 64, 96].forEach((v) => {
    ctx.beginPath();
    ctx.moveTo(0, y(v));
    ctx.lineTo(w, y(v));
    ctx.stroke();
  });

  const hline = (v, color, label) => {
    ctx.setLineDash([6, 6]);
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, y(v));
    ctx.lineTo(w, y(v));
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = color;
    ctx.font = '11px IBM Plex Mono';
    ctx.fillText(label, 8, y(v) - 4);
  };
  hline(th.ccBaseline + th.ccNoiseFloor, 'rgba(125,138,145,0.6)', `NOISE ${th.ccBaseline + th.ccNoiseFloor}`);
  const ts = spore.touch.getState();
  if (ts.rest && ts.detector.feature === 'ccDeparture') {
    const hi = Math.min(127, ts.rest.cc + ts.detector.thrOnScaled);
    const lo = Math.max(0, ts.rest.cc - ts.detector.thrOnScaled);
    ctx.fillStyle = ts.touching ? 'rgba(255,209,102,0.08)' : 'rgba(0,255,157,0.07)';
    ctx.fillRect(0, y(hi), w, Math.max(2, y(lo) - y(hi)));
    ctx.fillStyle = 'rgba(0,255,157,0.6)';
    ctx.fillText('RESTING PLATEAU ± TOUCH THRESHOLD', w - 270, y(hi) - 4);
  }
  if (th.pulseVelocity <= 127) hline(th.pulseVelocity, 'rgba(255,77,77,0.55)', `PULSE VEL ${th.pulseVelocity}`);

  spore.scope.gestures.forEach((g) => {
    const gx = x(g.t);
    ctx.strokeStyle = GESTURE_COLORS[g.type];
    ctx.globalAlpha = 0.5;
    ctx.beginPath();
    ctx.moveTo(gx, 0);
    ctx.lineTo(gx, h);
    ctx.stroke();
    ctx.globalAlpha = 1;
    ctx.fillStyle = GESTURE_COLORS[g.type];
    ctx.font = '600 11px IBM Plex Mono';
    ctx.fillText(g.type[0] + (g.seconds ? g.seconds : ''), gx + 4, 14);
  });

  spore.scope.notes.forEach((n) => {
    const nx = x(n.t);
    const strong = n.v >= th.pulseVelocity;
    ctx.strokeStyle = strong ? '#ffb000' : '#00ff9d';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(nx, h - 8);
    ctx.lineTo(nx, y(n.v));
    ctx.stroke();
    ctx.fillStyle = ctx.strokeStyle;
    ctx.beginPath();
    ctx.arc(nx, y(n.v), 3, 0, Math.PI * 2);
    ctx.fill();
  });

  ctx.lineWidth = 2;
  ctx.strokeStyle = '#00e5ff';
  ctx.shadowColor = '#00e5ff';
  ctx.shadowBlur = 12;
  ctx.beginPath();
  let started = false;
  spore.scope.cc.forEach((p) => {
    if (p.v === null) return;
    if (!started) {
      ctx.moveTo(x(p.t), y(p.v));
      started = true;
    } else ctx.lineTo(x(p.t), y(p.v));
  });
  ctx.stroke();
  ctx.shadowBlur = 0;
}

export const Oscilloscope = ({ snap }) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let raf;
    const loop = () => {
      const dpr = window.devicePixelRatio || 1;
      const { clientWidth: w, clientHeight: h } = canvas;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw(ctx, w, h);
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, []);

  const cc = snap.state.cc;
  return (
    <Panel
      testId="oscilloscope-panel"
      code="02 / BIO-SIGNAL"
      title="Live Oscilloscope · 8s"
      delay={80}
      right={
        <div className="flex gap-5 text-sm">
          <span className="glow-teal">CC90 <b data-testid="oscilloscope-cc-value">{cc === null ? '—' : cc}</b></span>
          <span className="glow-spore">VEL <b data-testid="oscilloscope-last-velocity">{snap.state.lastVelocity}</b></span>
        </div>
      }
    >
      <canvas ref={ref} data-testid="oscilloscope-canvas" className="h-[300px] w-full border border-[var(--hair)] bg-[#03060a]" />
      <div className="mt-3 flex flex-wrap gap-5 text-xs text-[var(--muted)]">
        <span><i className="mr-2 inline-block h-[2px] w-5 align-middle bg-[var(--teal)]" />CC90 sensor</span>
        <span><i className="mr-2 inline-block h-3 w-[2px] align-middle bg-[var(--spore)]" />gentle note</span>
        <span><i className="mr-2 inline-block h-3 w-[2px] align-middle bg-[var(--amber)]" />strong note</span>
        <span className="text-[var(--coral)]">P pulse</span>
        <span className="text-[var(--amber)]">S stroke</span>
        <span className="text-[var(--teal)]">S3/S6/S10 still</span>
      </div>
    </Panel>
  );
};
